import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  api,
  fetchAttendanceByMonth,
  fetchMentorshipSummary,
  AttendanceMonth,
  MentorshipSummary,
} from '../services/api';
import { Panel, PanelHeader, PanelBody, PageHeader } from '../components/ui/Panel';
import Button from '../components/ui/Button';
import Pill from '../components/ui/Pill';
import { BarRows, BarDatum, ColumnChart } from '../components/ui/Charts';
import { MiniKpiGrid } from '../components/ui/Kpi';
import { LoadingPanel, ErrorNotice, EmptyState } from '../components/ui/States';
import MentorshipReach from '../components/MentorshipReach';
import MoodleQuizzes from '../components/MoodleQuizzes';
import { fmt, pct, DASH } from '../lib/format';

const DIMENSIONS: { key: string; label: string }[] = [
  { key: 'genderIdentity', label: 'Gender identity' },
  { key: 'careerSeniority', label: 'Career stage' },
  { key: 'sector', label: 'Sector' },
  { key: 'geographicRegion', label: 'Geographic region' },
  { key: 'discipline', label: 'Discipline' },
  { key: 'preferredLanguage', label: 'Preferred language' },
  { key: 'preferredPronouns', label: 'Preferred pronouns' },
  { key: 'organization', label: 'Organization' },
  { key: 'province', label: 'Province' },
  { key: 'country', label: 'Country' },
  { key: 'healthProfessional', label: 'Health professional' },
];

const WINDOWS = [
  { key: 'all', label: 'Window: all time', months: null },
  { key: '12', label: 'Window: last 12 months', months: 12 },
  { key: '36', label: 'Window: last 3 years', months: 36 },
];

const COHORTS = [
  { key: 'all', label: 'Cohort: all members' },
  { key: 'year', label: 'Cohort: registered this year' },
];

interface DemographicsData {
  dimension: string;
  items: { value: string; count: number }[];
  totalProfiles: number;
  totalWithDimension: number;
}

interface EquityCategory {
  yes: number;
  answered: number;
}

interface EquityData {
  totalProfiles: number;
  categories: {
    indigenous: EquityCategory;
    racialized: EquityCategory;
    disability: EquityCategory;
    twoSLGBQA: EquityCategory;
    socioEconomic: EquityCategory;
    gender: {
      answered: number;
      breakdown: { value: string; count: number }[];
    };
  };
}

const TRANS_NB = /trans|non-binary|nonbinary|non-conforming|two.?spirit|bispirit/i;

const selectClass = 'border border-line bg-white px-3 py-[9px] text-table';

const ratePct = (value: number | null) => (value === null ? DASH : `${value}%`);

const headCell = 'font-semibold text-small text-ink-2 py-[10px]';

function monthLabel(month: string) {
  const [year, m] = month.split('-');
  const d = new Date(Date.UTC(Number(year), Number(m) - 1, 1));
  if (Number.isNaN(d.getTime())) return month;
  const name = d.toLocaleString('en-CA', { month: 'short', timeZone: 'UTC' });
  return `${name} ${year.slice(2)}`;
}

function EquityTile({ label, count, answered }: { label: string; count: number; answered: number }) {
  const share = answered > 0 ? (count / answered) * 100 : 0;
  return (
    <div className="bg-white px-5 py-4" title={`${fmt(count)} of ${fmt(answered)} who answered`}>
      <div className="font-semibold text-small text-ink-2">{label}</div>
      <div className="flex items-baseline gap-2 mt-2">
        <span className="font-semibold tabular text-[36px] leading-none">{fmt(count)}</span>
        <span className="text-table text-ink-2 tabular">{answered > 0 ? pct(count, answered) : DASH}</span>
      </div>
      <div className="bg-paper mt-3" style={{ height: 6 }}>
        <div className="bg-accent h-full" style={{ width: `${share}%` }} />
      </div>
    </div>
  );
}

export default function Analytics() {
  const [dimension, setDimension] = useState('genderIdentity');
  const [win, setWin] = useState('all');
  const [cohort, setCohort] = useState('all');
  const [tableView, setTableView] = useState(false);

  const since = useMemo(() => {
    if (cohort === 'year') return new Date(new Date().getFullYear(), 0, 1).toISOString();
    const months = WINDOWS.find((w) => w.key === win)?.months;
    if (!months) return undefined;
    const d = new Date();
    d.setMonth(d.getMonth() - months);
    return d.toISOString();
  }, [win, cohort]);

  const params = { since };

  const demographics = useQuery<DemographicsData>(['demographics', dimension, since], async () => {
    const res = await api.get('/analytics/demographics', { params: { dimension, ...params } });
    return res.data.data;
  });

  const clusters = useQuery<DemographicsData>(['demographics', 'geographicRegion', since], async () => {
    const res = await api.get('/analytics/demographics', {
      params: { dimension: 'geographicRegion', ...params },
    });
    return res.data.data;
  });

  const equity = useQuery<EquityData>(['equity', since], async () => {
    const res = await api.get('/analytics/equity-summary', { params });
    return res.data.data;
  });

  const attendance = useQuery<AttendanceMonth[]>('attendance-by-month', () => fetchAttendanceByMonth());

  const attendanceMonths = attendance.data ?? [];
  const attendanceTotal = attendanceMonths.reduce((sum, m) => sum + m.live + m.recording, 0);

  const mentorship = useQuery<MentorshipSummary>('mentorship-summary', () => fetchMentorshipSummary());

  const mentorshipSeasons = mentorship.data?.seasons ?? [];
  const mentorshipTotals = mentorship.data?.totals;

  const d = demographics.data;
  const dimensionLabel = DIMENSIONS.find((x) => x.key === dimension)?.label ?? dimension;

  const bars: BarDatum[] = (d?.items ?? []).map((i) => ({
    label: i.value,
    value: i.count,
  }));

  const exportCsv = () => {
    if (!d) return;
    const rows = [
      ['Category', 'Count', 'Share'],
      ...d.items.map((i) => [i.value, String(i.count), d.totalWithDimension ? pct(i.count, d.totalWithDimension) : '']),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `divert-${dimension}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const eq = equity.data?.categories;

  const transNonBinary = useMemo(() => {
    const breakdown = eq?.gender.breakdown ?? [];
    return breakdown.filter((b) => TRANS_NB.test(b.value)).reduce((sum, b) => sum + b.count, 0);
  }, [eq]);

  return (
    <>
      <PageHeader
        title="Analytics"
        sub="Membership demographics and equity aggregates, plus event attendance and mentorship participation across the full history."
      />

      <Panel className="mb-6">
        <div className="flex flex-wrap items-center gap-[10px] px-5 py-[14px]">
          <select
            value={dimension}
            onChange={(e) => setDimension(e.target.value)}
            className="border border-line-strong bg-white px-3 py-[9px] text-table min-w-[220px]"
          >
            {DIMENSIONS.map((x) => (
              <option key={x.key} value={x.key}>
                {x.label}
              </option>
            ))}
          </select>
          <select value={cohort} onChange={(e) => setCohort(e.target.value)} className={selectClass}>
            {COHORTS.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
          <select
            value={win}
            onChange={(e) => setWin(e.target.value)}
            disabled={cohort === 'year'}
            className={`${selectClass} disabled:opacity-45`}
          >
            {WINDOWS.map((w) => (
              <option key={w.key} value={w.key}>
                {w.label}
              </option>
            ))}
          </select>
          <Button variant="outline" size="sm" onClick={() => setTableView((v) => !v)}>
            {tableView ? 'Chart view' : 'Table view'}
          </Button>
          <div className="ml-auto">
            <Button variant="dark" size="sm" onClick={exportCsv} disabled={!d}>
              Export CSV
            </Button>
          </div>
        </div>
      </Panel>

      {demographics.error ? <ErrorNotice>Could not load demographics. Retry in a moment.</ErrorNotice> : null}

      <Panel>
        <div className="flex items-start justify-between gap-6 px-5 py-4 border-b border-line-strong">
          <div>
            <h2 className="font-serif font-semibold text-[24px] leading-none">{dimensionLabel}</h2>
            <p className="text-small text-ink-2 mt-2">
              {d
                ? `${fmt(d.totalWithDimension)} of ${fmt(d.totalProfiles)} members with this dimension`
                : 'Loading coverage…'}
            </p>
          </div>
          <span className="font-semibold tabular text-[34px] leading-none">{d ? fmt(d.totalWithDimension) : ''}</span>
        </div>

        <PanelBody>
          {demographics.isLoading ? (
            <LoadingPanel label="Loading demographics…" />
          ) : bars.length === 0 ? (
            <EmptyState title="No data for this dimension">
              No member profile carries a value for this dimension yet. Choose another dimension, or widen the window.
            </EmptyState>
          ) : tableView ? (
            <>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-line">
                    <th className="font-semibold text-small text-ink-2 text-left py-[10px]">Category</th>
                    <th className="font-semibold text-small text-ink-2 text-right py-[10px]">Count</th>
                    <th className="font-semibold text-small text-ink-2 text-right py-[10px]">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {d?.items.map((i) => (
                    <tr key={i.value} className="border-b border-line last:border-b-0">
                      <td className="py-[10px] text-table">{i.value}</td>
                      <td className="py-[10px] text-table text-right tabular">{fmt(i.count)}</td>
                      <td className="py-[10px] text-table text-right tabular text-ink-2">
                        {d.totalWithDimension ? pct(i.count, d.totalWithDimension) : DASH}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <BarRows data={bars} />
          )}
        </PanelBody>
      </Panel>

      <Panel className="mt-6">
        <PanelHeader title="Equity summary" note={eq ? 'Share of those who answered each question' : undefined} />
        {equity.isLoading || !eq ? (
          <LoadingPanel label="Loading equity summary…" height={200} />
        ) : (
          <div className="grid grid-cols-3 gap-px bg-line">
            <EquityTile label="Indigenous" count={eq.indigenous.yes} answered={eq.indigenous.answered} />
            <EquityTile label="Racialized" count={eq.racialized.yes} answered={eq.racialized.answered} />
            <EquityTile label="Disability" count={eq.disability.yes} answered={eq.disability.answered} />
            <EquityTile label="2SLGBQA+" count={eq.twoSLGBQA.yes} answered={eq.twoSLGBQA.answered} />
            <EquityTile
              label="Socio-economic disadvantage"
              count={eq.socioEconomic.yes}
              answered={eq.socioEconomic.answered}
            />
            <EquityTile label="Gender (trans & non-binary)" count={transNonBinary} answered={eq.gender.answered} />
          </div>
        )}
      </Panel>

      <Panel className="mt-6">
        <PanelHeader title="Members by cluster" note="Geographic region" />
        <PanelBody>
          {clusters.isLoading || !clusters.data ? (
            <LoadingPanel label="Loading clusters…" height={180} />
          ) : (
            <BarRows
              track={22}
              data={clusters.data.items.map((i) => ({
                label: i.value,
                value: i.count,
              }))}
            />
          )}
        </PanelBody>
      </Panel>

      <Panel className="mt-6">
        <PanelHeader title="Event attendance by month" note="All history" />
        {attendance.isLoading ? (
          <LoadingPanel label="Loading event attendance…" height={220} />
        ) : attendance.error ? (
          <PanelBody>
            <ErrorNotice>Could not load event attendance. Retry in a moment.</ErrorNotice>
          </PanelBody>
        ) : attendanceMonths.length === 0 ? (
          <EmptyState title="No attendance recorded">
            No live or recorded event attendance has been ingested yet. Records arrive from the attendance sync once a
            session has been held.
          </EmptyState>
        ) : (
          <>
            <MiniKpiGrid
              cells={[
                { label: 'Attendance events, all time', value: fmt(attendanceTotal) },
                { label: 'Months with attendance', value: fmt(attendanceMonths.length) },
              ]}
            />
            <PanelBody>
              <ColumnChart
                data={attendanceMonths.map((m) => ({
                  label: monthLabel(m.month),
                  value: m.live + m.recording,
                }))}
              />
            </PanelBody>
          </>
        )}
      </Panel>

      <Panel className="mt-6">
        <PanelHeader title="Mentorship by season" note="All seasons" />
        {mentorship.isLoading ? (
          <LoadingPanel label="Loading mentorship…" height={220} />
        ) : mentorship.error ? (
          <PanelBody>
            <ErrorNotice>Could not load mentorship data. Retry in a moment.</ErrorNotice>
          </PanelBody>
        ) : mentorshipSeasons.length === 0 || !mentorshipTotals ? (
          <EmptyState title="No mentorship participation">
            No mentor or mentee records have been ingested yet. Rows arrive from the mentorship sync once a season opens
            for preferences.
          </EmptyState>
        ) : (
          <>
            <MiniKpiGrid
              cells={[
                { label: 'Mentors, all seasons', value: fmt(mentorshipTotals.mentors) },
                { label: 'Mentees, all seasons', value: fmt(mentorshipTotals.mentees) },
                {
                  label: `Sessions attended of ${fmt(mentorshipTotals.sessionsScheduled)} scheduled`,
                  value: fmt(mentorshipTotals.sessionsAttended),
                },
                {
                  label: 'Session attendance rate',
                  value: ratePct(mentorshipTotals.attendanceRate),
                },
              ]}
            />
            <PanelBody>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-line">
                    <th className={`${headCell} text-left`}>Season</th>
                    <th className={`${headCell} text-right`}>Mentors</th>
                    <th className={`${headCell} text-right`}>Mentees</th>
                    <th className={`${headCell} text-right`}>Paired</th>
                    <th className={`${headCell} text-right`}>Pairing rate</th>
                    <th className={`${headCell} text-right`}>Sessions</th>
                    <th className={`${headCell} text-right`}>Attendance rate</th>
                    <th className={`${headCell} text-right`}>Affinity matched</th>
                  </tr>
                </thead>
                <tbody>
                  {mentorshipSeasons.map((s) => (
                    <tr key={s.seasonId} className="border-b border-line last:border-b-0">
                      <td className="py-[10px] text-table">
                        <span className="flex items-center gap-2">
                          {s.seasonName}
                          {s.seasonIsActive ? <Pill tone="active">Active</Pill> : null}
                        </span>
                      </td>
                      <td className="py-[10px] text-table text-right tabular">{fmt(s.mentors)}</td>
                      <td className="py-[10px] text-table text-right tabular">{fmt(s.mentees)}</td>
                      <td className="py-[10px] text-table text-right tabular">{fmt(s.menteesPaired)}</td>
                      <td className="py-[10px] text-table text-right tabular text-ink-2">{ratePct(s.pairingRate)}</td>
                      <td className="py-[10px] text-table text-right tabular">
                        {fmt(s.sessionsAttended)} / {fmt(s.sessionsScheduled)}
                      </td>
                      <td className="py-[10px] text-table text-right tabular text-ink-2">
                        {ratePct(s.attendanceRate)}
                      </td>
                      <td className="py-[10px] text-table text-right tabular text-ink-2">
                        {s.matchQuality ? ratePct(s.matchQuality.affinityMatchedRate) : DASH}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-small text-ink-2 mt-4 pt-3 border-t border-line">
                Pairing rate is the share of mentees with at least one pairing. Sessions are counted once per pairing,
                on the mentor side, so they are not double-counted across roles. Mentees an app admin removed before
                matching are not counted. Affinity matched is the share of pairs where the app’s own suggestion flagged
                an affinity match.
              </p>
            </PanelBody>
          </>
        )}
      </Panel>

      {mentorshipSeasons.length > 0 ? <MentorshipReach seasons={mentorshipSeasons} /> : null}

      <MoodleQuizzes />
    </>
  );
}
